import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { ButtonSecondary } from "@/components/Buttons";
import { Scale, Eye, ShieldCheck, BookOpen } from "lucide-react";

const VALUES = [
    {
        icon: Scale,
        title: "Quality",
        text: "Quality is a process, not a promise. Materials are sourced, handled and released against defined standards — every time.",
    },
    {
        icon: Eye,
        title: "Transparency",
        text: "We document what we supply and how it was verified, and we make that documentation available. Nothing important stays hidden.",
    },
    {
        icon: ShieldCheck,
        title: "Integrity",
        text: "Independent testing means exactly that. Verification comes from laboratories that do not answer to us.",
    },
    {
        icon: BookOpen,
        title: "Responsibility",
        text: "Our materials are supplied strictly for laboratory research use. We communicate clearly about what they are — and what they are not.",
    },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <PageHeader
                eyebrow="Our Company"
                title="About Grayhaven"
                description="A research materials company built on a simple conviction: quality should never be a question."
            />

            {/* Story */}
            <section className="pb-24 sm:pb-32 px-6 lg:px-8">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <Reveal>
                        <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-brand">
                            Research materials, held to a higher standard
                        </h2>
                        <div className="mt-8 space-y-6 text-base leading-relaxed text-mist">
                            <p>
                                Grayhaven Research was established around a frustration shared by many in the research
                                community: sourcing materials often means taking a supplier's word for quality. We
                                believed there was a better way to operate — one where verification is independent,
                                documentation is standard, and transparency is the default rather than the exception.
                            </p>
                            <p>
                                That belief shapes everything we do. Our suppliers are vetted before their materials
                                are considered. Our materials are tested by independent third-party laboratories. And
                                our documentation — Certificates of Analysis, batch and lot records — is maintained so
                                that every material we supply can be traced and verified.
                            </p>
                            <p>
                                We communicate carefully and precisely. Our materials are supplied strictly for
                                laboratory research use, and we do not make claims beyond what our documentation
                                supports.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="relative">
                            <div className="absolute -inset-3 border border-gold/25 rounded-sm" aria-hidden="true" />
                            <img
                                src="https://images.unsplash.com/photo-1582719299074-be127353065f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBzY2llbnRpZmljJTIwbGFib3JhdG9yeSUyMGNsZWFufGVufDB8fHx8MTc5MTMwNzAyMXww&ixlib=rb-4.1.0&q=85"
                                alt="Clean scientific laboratory environment"
                                className="relative w-full h-[460px] object-cover rounded-sm border border-stone-200 shadow-[0_16px_50px_rgba(44,58,78,0.06)]"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Mission */}
            <section className="py-24 sm:py-28 px-6 lg:px-8 bg-white/70 border-y border-stone-200">
                <Reveal className="max-w-4xl mx-auto">
                    <p className="eyebrow text-center">Our Mission</p>
                    <blockquote
                        data-testid="mission-statement"
                        className="mt-10 border-l-2 border-gold pl-8 sm:pl-12 font-serif text-2xl sm:text-3xl font-light italic leading-snug text-slate-700"
                    >
                        “At Grayhaven Research, we believe quality should never be a question. Our mission is to
                        provide carefully sourced, independently tested research materials with a commitment to
                        transparency, consistency, and scientific integrity.”
                    </blockquote>
                </Reveal>
            </section>

            {/* Values */}
            <section className="py-24 sm:py-32 px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <Reveal className="max-w-2xl">
                        <p className="eyebrow">What We Stand For</p>
                        <h2 className="mt-6 font-serif text-3xl sm:text-4xl font-light tracking-tight text-brand">
                            Four principles, no exceptions
                        </h2>
                    </Reveal>
                    <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {VALUES.map((value, i) => (
                            <Reveal key={value.title} delay={i * 0.1}>
                                <div
                                    data-testid={`value-card-${i}`}
                                    className="card-lift h-full bg-surface border border-gold/20 p-8 rounded-sm hover:border-gold/50 hover:bg-surface2"
                                >
                                    <value.icon className="h-7 w-7 text-gold" strokeWidth={1.5} />
                                    <h3 className="mt-6 font-serif text-2xl font-light text-brand">{value.title}</h3>
                                    <p className="mt-4 text-sm leading-relaxed text-mist">{value.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-16 text-center">
                        <ButtonSecondary to="/standards" data-testid="about-standards-btn">
                            Explore Our Standards
                        </ButtonSecondary>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
